import React from 'react';
import { Card, CardContent, Typography, Chip, Button } from '@mui/material';
import { Computer, Server, Globe, Wifi, WifiOff, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '@common-reducers/hooks/store.hook';
import { selectConnectedEnginesSupport, ConnectedSocket } from '@common/reducers/ConnectedEnginesSlice';

interface ConnectedEnginesSupportProps {
    userId: string;
}

// For support page, we only deal with real ConnectedSocket engines from EMQX
type Engine = ConnectedSocket;

const getEngineTypeInfo = (engine: Engine) => {
    if (!engine) return null;

    // Handle ConnectedSocket engines from EMQX API
    if ('browserType' in engine) {
        switch (engine.browserType) {
            case 'Gateway':
                return {
                    type: 'gateway',
                    label: 'Gateway',
                    icon: Server,
                    color: 'bg-blue-100 text-blue-800 border-blue-200',
                    iconColor: 'text-blue-600',
                    bgColor: 'bg-blue-50'
                };
            case 'Website':
                return {
                    type: 'frontend',
                    label: 'Website',
                    icon: Computer,
                    color: 'bg-orange-100 text-orange-800 border-orange-200',
                    iconColor: 'text-orange-600',
                    bgColor: 'bg-orange-50'
                };
            case 'WhatsApp Web':
                return {
                    type: 'whatsapp-web',
                    label: 'WhatsApp Web',
                    icon: Globe,
                    color: 'bg-green-100 text-green-800 border-green-200',
                    iconColor: 'text-green-600',
                    bgColor: 'bg-green-50'
                };
            default:
                return {
                    type: 'unknown',
                    label: engine.browserType || 'Unknown',
                    icon: Computer,
                    color: 'bg-gray-100 text-gray-800 border-gray-200',
                    iconColor: 'text-gray-600',
                    bgColor: 'bg-gray-50'
                };
        }
    }

    return null;
};

const formatConnectionTime = (connectionTime: string) => {
    try {
        const date = new Date(connectionTime);
        return date.toLocaleString('en-US', {
            month: 'numeric',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    } catch (error) {
        return 'Unknown';
    }
};

export function ConnectedEnginesSupport({ userId }: ConnectedEnginesSupportProps) {
    const engines: Engine[] = useAppSelector(selectConnectedEnginesSupport);
    const navigate = useNavigate();
    
    // Get current URL search params to preserve supportedUserId
    const searchParams = new URLSearchParams(window.location.search);
    const supportedUserId = searchParams.get('supportedUserId');

    if (engines.length === 0) {
        return (
            <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                    <WifiOff className="h-5 w-5 text-gray-500" />
                    <Typography variant="h6" className="text-gray-700 font-semibold">
                        Connected Engines
                    </Typography>
                </div>
                <div className="flex flex-col items-center justify-center py-8 px-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                    <WifiOff className="h-12 w-12 text-gray-400 mb-3" />
                    <Typography variant="body1" className="text-gray-500 text-center">
                        No engines currently connected
                    </Typography>
                    <Typography variant="body2" className="text-gray-400 text-center mt-1">
                        Engines will appear here when the user connects to the platform
                    </Typography>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <Wifi className="h-5 w-5 text-green-500" />
                    <Typography variant="h6" className="text-gray-700 font-semibold">
                        Connected Engines
                    </Typography>
                </div>
                <Chip
                    label={`${engines.length} engine${engines.length !== 1 ? 's' : ''} connected`}
                    size="small"
                    className="bg-green-100 text-green-800"
                />
            </div>

            {/* Responsive Grid Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {engines.map((engine) => {
                    const engineInfo = getEngineTypeInfo(engine);
                    if (!engineInfo) return null;

                    const Icon = engineInfo.icon;
                    const connectionTime = engine.connectionTime;
                    const domain = engine.domain;
                    const ip = engine.ip;

                    return (
                        <Card 
                            key={engine.id} 
                            className={`${engineInfo.bgColor} border-l-4 border-l-current transition-all duration-200 hover:shadow-md hover:scale-[1.02]`}
                        >
                            <CardContent className="p-5">
                                <div className="flex flex-col gap-3">
                                    <div className="flex items-center gap-3 flex-1">
                                        <div className={`p-3 rounded-lg ${engineInfo.color.replace('text-', 'bg-').replace('border-', 'bg-').split(' ')[0]}`}>
                                            <Icon className={`h-6 w-6 ${engineInfo.iconColor}`} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-2">
                                                <Typography variant="subtitle1" className="font-semibold text-gray-900 truncate">
                                                    {engineInfo.label}
                                                </Typography>
                                                <Chip
                                                    label={engineInfo.type}
                                                    size="small"
                                                    className={`${engineInfo.color} text-xs`}
                                                />
                                            </div>
                                            <Typography variant="body2" className="text-gray-600 block truncate mb-1">
                                                {domain}
                                            </Typography>
                                            <Typography variant="caption" className="text-gray-500 block">
                                                Connected: {formatConnectionTime(connectionTime)}
                                            </Typography>
                                            {ip && (
                                                <Typography variant="caption" className="text-gray-400 block">
                                                    IP: {ip}
                                                </Typography>
                                            )}
                                            {engine.clientId && (
                                                <Typography variant="caption" className="text-gray-400 block font-mono" style={{ fontSize: '0.65rem' }}>
                                                    {engine.clientId}
                                                </Typography>
                                            )}
                                        </div>
                                    </div>
                                    
                                    {/* Show explore button for WhatsApp Web and Gateway engines */}
                                    {(engineInfo.type === 'whatsapp-web' || engineInfo.type === 'gateway') && (
                                        <div className="flex justify-end pt-2">
                                            <Button
                                                variant="contained"
                                                size="medium"
                                                endIcon={<ArrowRight className="h-4 w-4" />}
                                                onClick={() => {
                                                    const enginePath = `/engine-explorer/${engine.id}`;
                                                    const navigationUrl = supportedUserId 
                                                        ? `${enginePath}?supportedUserId=${supportedUserId}`
                                                        : enginePath;
                                                    navigate(navigationUrl);
                                                }}
                                                className="font-medium"
                                                sx={{
                                                    textTransform: 'none',
                                                    borderRadius: '8px',
                                                    backgroundColor: engineInfo.type === 'whatsapp-web' ? '#22c55e' : '#3b82f6',
                                                    '&:hover': {
                                                        backgroundColor: engineInfo.type === 'whatsapp-web' ? '#16a34a' : '#2563eb',
                                                    },
                                                    padding: '8px 16px',
                                                    fontSize: '0.875rem'
                                                }}
                                            >
                                                Explore Engine
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>
        </div>
    );
} 